import api from './api';

const TOKEN_KEY = 'accessToken';
const REFRESH_TOKEN_KEY = 'refreshToken';
const USER_KEY = 'user';

class AuthService {
  async login(username, password, organizationSlug = null) {
    // This call uses the default baseURL which includes /api
    const requestData = {
      username,
      password,
    };
    
    // Add organization context if provided (for whitelabel login)
    if (organizationSlug) {
      requestData.organization = organizationSlug;
    }
    
    const response = await api.post('/token/', requestData);
    
    if (response.data.access) {
      this.setToken(response.data.access);
      this.setRefreshToken(response.data.refresh);
      
      // Store organization context if available in response
      if (response.data.organization) {
        localStorage.setItem('organization', JSON.stringify(response.data.organization));
      }
      
      // After setting the token, fetch the user details
      await this.fetchAndStoreUser();
    }
    return response.data;
  }

  async fetchAndStoreUser() {
    try {
      // This call should use the default /api baseURL, not the ROOT_URL override.
      const { data: user } = await api.get('/users/me/');
      this.setUser(user);
    } catch (e) {
      console.error("Failed to fetch user data after login", e);
      this.logout(); // Logout if we can't get user data
    }
  }

  register(userData) {
    // For this call, we hit /api/users/register/
    return api.post('/users/register/', userData);
  }

  getRoles() {
    // For this call, we hit /api/users/roles/
    return api.get('/users/roles/');
  }

  // === OAUTH AUTHENTICATION METHODS ===

  async loginWithGoogle(accessToken, organizationSlug = null) {
    const requestData = {
      access_token: accessToken,
    };
    
    // Add organization slug for whitelabel assignment
    if (organizationSlug) {
      requestData.organization_slug = organizationSlug;
    }
    
    const response = await api.post('/users/auth/google/', requestData);
    
    if (response.data.access) {
      this.setToken(response.data.access);
      this.setRefreshToken(response.data.refresh);
      this.setUser(response.data.user);
    }
    return response.data;
  }

  async loginWithMicrosoft(accessToken, organizationSlug = null) {
    const requestData = {
      access_token: accessToken,
    };
    
    // Add organization slug for whitelabel assignment
    if (organizationSlug) {
      requestData.organization_slug = organizationSlug;
    }
    
    const response = await api.post('/users/auth/microsoft/', requestData);
    
    if (response.data.access) {
      this.setToken(response.data.access);
      this.setRefreshToken(response.data.refresh);
      this.setUser(response.data.user);
    }
    return response.data;
  }

  async loginWithLinkedIn(accessToken) {
    const response = await api.post('/users/auth/linkedin/', {
      access_token: accessToken,
    });
    
    if (response.data.access) {
      this.setToken(response.data.access);
      this.setRefreshToken(response.data.refresh);
      this.setUser(response.data.user);
    }
    return response.data;
  }

  async linkProvider(provider, accessToken, identityToken = '') {
    const body = { provider };
    if (identityToken) {
      body.identity_token = identityToken;
    } else {
      body.access_token = accessToken;
    }
    const response = await api.post('/users/me/auth-identities/link/', body);
    await this.fetchAndStoreUser();
    return response.data;
  }

  async unlinkProvider(identityId) {
    await api.delete(`/users/me/auth-identities/${identityId}/`);
    await this.fetchAndStoreUser();
  }

  async removePasswordLogin() {
    const response = await api.post('/users/me/auth-identities/remove-password/');
    await this.fetchAndStoreUser();
    return response.data;
  }

  async loginWithApple(identityToken, firstName = '', lastName = '') {
    const response = await api.post('/users/auth/apple/', {
      identity_token: identityToken,
      first_name: firstName,
      last_name: lastName,
    });
    
    if (response.data.access) {
      this.setToken(response.data.access);
      this.setRefreshToken(response.data.refresh);
      this.setUser(response.data.user);
    }
    return response.data;
  }

  logout() {
    // Get user info before clearing to check OAuth provider
    const user = this.getUser();
    
    // Clear local tokens and user data
    this.removeToken();
    this.removeRefreshToken();
    this.removeUser();
    
    // Handle OAuth provider logout
    if (user && user.oauth_provider) {
      this.handleOAuthLogout(user.oauth_provider);
    }
  }

  handleOAuthLogout(provider) {
    try {
      switch (provider) {
        case 'google':
          this.logoutFromGoogle();
          break;
        case 'microsoft':
          this.logoutFromMicrosoft();
          break;
        case 'linkedin':
          this.logoutFromLinkedIn();
          break;
        case 'apple':
          this.logoutFromApple();
          break;
        default:
          console.log('No OAuth provider logout needed');
      }
    } catch (error) {
      console.error(`Error during ${provider} logout:`, error);
      // Don't throw error - local logout should still work
    }
  }

  logoutFromGoogle() {
    // Google Sign-Out
    if (window.google && window.google.accounts) {
      try {
        window.google.accounts.id.disableAutoSelect();
        // Revoke the token if available
        const token = this.getToken();
        if (token) {
          window.google.accounts.oauth2.revoke(token);
        }
      } catch (error) {
        console.error('Google logout error:', error);
      }
    }
  }

  logoutFromMicrosoft() {
    // Microsoft Sign-Out
    if (window.msal) {
      try {
        const accounts = window.msal.getAllAccounts();
        if (accounts.length > 0) {
          window.msal.logoutPopup({
            account: accounts[0],
            mainWindowRedirectUri: window.location.origin
          });
        }
      } catch (error) {
        console.error('Microsoft logout error:', error);
      }
    }
  }

  logoutFromLinkedIn() {
    // LinkedIn doesn't have a JavaScript logout API
    // Clear any stored LinkedIn state
    localStorage.removeItem('linkedin_oauth_state');
    console.log('LinkedIn logout: cleared local state');
  }

  logoutFromApple() {
    // Apple Sign-In logout (when implemented)
    console.log('Apple logout: cleared local state');
  }

  async refreshToken() {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken) {
      console.error("AuthService: No refresh token available.");
      throw new Error('No refresh token found');
    }
    
    console.log("AuthService: Attempting to refresh token via API.");
    try {
      const response = await api.post('/token/refresh/', {
      refresh: refreshToken,
      });

      if (response.data.access) {
        console.log("AuthService: New access token received.");
        this.setToken(response.data.access);
      } else {
        console.warn("AuthService: Refresh token endpoint did not return new access token.");
      }
      // Note: If ROTATE_REFRESH_TOKENS were true, we'd handle response.data.refresh here.

      return response.data;
    } catch (error) {
      console.error('AuthService: Error during token refresh.', error);
      // We explicitly throw the error so the interceptor in api.js can catch it.
      throw error;
    }
  }

  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  }

  setToken(token) {
    localStorage.setItem(TOKEN_KEY, token);
    // Note: notifyAuthStateChanged called in setUser which happens after setToken
  }

  removeToken() {
    localStorage.removeItem(TOKEN_KEY);
    // Dispatch event for logout scenarios
    this.notifyAuthStateChanged();
  }

  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  }

  setRefreshToken(token) {
    localStorage.setItem(REFRESH_TOKEN_KEY, token);
  }

  removeRefreshToken() {
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  isAuthenticated() {
    return !!this.getToken();
  }

  setUser(user) {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    // Dispatch event to notify RoleGuard and other components of auth state change
    this.notifyAuthStateChanged();
  }

  getUser() {
    const user = localStorage.getItem(USER_KEY);
    return user ? JSON.parse(user) : null;
  }

  removeUser() {
    localStorage.removeItem(USER_KEY);
    // Dispatch event to notify RoleGuard and other components of auth state change
    this.notifyAuthStateChanged();
  }
  
  /**
   * Notify all listeners that auth state has changed
   * Used by RoleGuard to re-evaluate permissions
   */
  notifyAuthStateChanged() {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth-state-changed'));
    }
  }

  // === ROLE CONSTANTS ===
  
  // Platform roles (Praetorian AI staff only)
  PLATFORM_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER'];

  // ADI compliance operations (platform org; simplified nav)
  COMPLIANCE_OPS_ROLES = ['COMPLIANCE_OPS'];
  
  // Admin roles (can manage organization)
  ADMIN_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'ADMIN'];
  
  // LMS admin roles (can manage courses, learners)
  LMS_ADMIN_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'LMS_ADMIN', 'ADMIN'];
  
  // Compliance admin roles (can manage subjects, reports)
  COMPLIANCE_ADMIN_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'RISK_MANAGER'];
  
  // Roles that can write/modify data
  WRITE_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'LMS_ADMIN', 'COMPLIANCE_ADMIN', 'ANALYST', 
                 'ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER'];
  
  // Roles that can access LMS features
  LMS_ACCESS_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'LMS_ADMIN', 'COMPLIANCE_ADMIN', 
                      'ANALYST', 'LEARNER', 'ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER', 'STUDENT'];
  
  // Roles that can access compliance features
  COMPLIANCE_ACCESS_ROLES = ['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'COMPLIANCE_OPS', 'ORG_ADMIN', 'COMPLIANCE_ADMIN', 
                             'ANALYST', 'VIEWER', 'ADMIN', 'RISK_MANAGER', 'CLIENT_MANAGER', 'REVIEWER', 'READ_ONLY'];
  
  // Deprecated role mapping
  DEPRECATED_ROLE_MAP = {
    'ADMIN': 'ORG_ADMIN',
    'RISK_MANAGER': 'COMPLIANCE_ADMIN',
    'CLIENT_MANAGER': 'ANALYST',
    'REVIEWER': 'VIEWER',
    'READ_ONLY': 'VIEWER',
    'STUDENT': 'LEARNER',
  };

  // === ROLE-BASED UTILITIES ===
  
  /**
   * Get the current user's role
   * @returns {string|null} User role or null if not authenticated
   */
  getUserRole() {
    const user = this.getUser();
    return user ? user.role : null;
  }

  /**
   * Get the effective role (maps deprecated roles to new ones)
   * @returns {string|null} Effective role or null if not authenticated
   */
  getEffectiveRole() {
    const role = this.getUserRole();
    if (!role) return null;
    return this.DEPRECATED_ROLE_MAP[role] || role;
  }

  /**
   * Get the current user's organization
   * @returns {Object|null} Organization object or null if not authenticated
   */
  getUserOrganization() {
    const user = this.getUser();
    return user ? user.organization : null;
  }

  /**
   * Check if user has a specific role (includes deprecated role mapping)
   * @param {string} role - Role to check (e.g., 'ORG_ADMIN', 'PLATFORM_ADMIN')
   * @returns {boolean}
   */
  hasRole(role) {
    const userRole = this.getUserRole();
    if (userRole === role) return true;
    // Check if the user's deprecated role maps to the requested role
    if (this.DEPRECATED_ROLE_MAP[userRole] === role) return true;
    return false;
  }

  /**
   * Check if user has any of the specified roles
   * @param {string[]} roles - Array of roles to check
   * @returns {boolean}
   */
  hasAnyRole(roles) {
    const userRole = this.getUserRole();
    if (!userRole) return false;
    if (roles.includes(userRole)) return true;
    // Check if the user's deprecated role maps to any requested role
    const effectiveRole = this.DEPRECATED_ROLE_MAP[userRole];
    if (effectiveRole && roles.includes(effectiveRole)) return true;
    return false;
  }

  /**
   * Check if user is a platform user (Praetorian AI staff)
   * @returns {boolean}
   */
  isPlatformUser() {
    const org = this.getUserOrganization();
    return org && org.organization_type === 'PLATFORM';
  }

  /**
   * Check if user is a customer user
   * @returns {boolean}
   */
  isCustomerUser() {
    const org = this.getUserOrganization();
    return org && org.organization_type === 'CUSTOMER';
  }

  /**
   * Check if user has platform-level access (Praetorian AI staff only)
   * @returns {boolean}
   */
  hasPlatformAccess() {
    return this.hasAnyRole(this.PLATFORM_ROLES);
  }

  /**
   * ADI compliance operations partner — simplified nav, cross-org compliance work.
   * @returns {boolean}
   */
  isComplianceOps() {
    return this.hasAnyRole(this.COMPLIANCE_OPS_ROLES);
  }

  /**
   * Agent Activity, Growth Contacts, hot-lead follow-up.
   * @returns {boolean}
   */
  canViewGrowthEngine() {
    return this.hasPlatformAccess() || this.isComplianceOps();
  }

  /**
   * Account Marketing hub — growth feed, contacts, quotes.
   * Broader than growth-engine APIs so org admins can reach Quote Builder.
   * @returns {boolean}
   */
  canAccessMarketing() {
    return (
      this.canViewGrowthEngine()
      || this.hasAnyRole(['ORG_ADMIN', 'HEALTHCARE_ADMIN', 'COMPLIANCE_ADMIN', 'ADMIN', 'LMS_ADMIN'])
    );
  }

  /**
   * Comprehensive check definitions, Check Lab, AI Check Builder.
   * @returns {boolean}
   */
  canConfigureChecks() {
    return this.hasPlatformAccess() || this.isComplianceOps();
  }

  /**
   * Cross-org clients, projects, and check definitions.
   * @returns {boolean}
   */
  hasCrossOrgComplianceAccess() {
    if (this.hasPlatformAccess()) return true;
    return this.isComplianceOps() && this.isPlatformUser();
  }

  /**
   * Get user display name with role
   * @returns {string} Formatted display name
   */
  getUserDisplayName() {
    const user = this.getUser();
    if (!user) return 'Unknown User';
    
    const name = user.first_name && user.last_name 
      ? `${user.first_name} ${user.last_name}`
      : user.username;
    
    return `${name} (${this.getRoleDisplayName()})`;
  }

  /**
   * Get organization display name
   * @returns {string} Organization name
   */
  getOrganizationName() {
    const org = this.getUserOrganization();
    return org ? org.name : 'Unknown Organization';
  }

  /**
   * Get human-readable role name
   * @returns {string} Role display name
   */
  getRoleDisplayName() {
    const roleMap = {
      // Platform Roles
      'PLATFORM_ADMIN': 'Platform Admin',
      'PLATFORM_ENGINEER': 'Platform Engineer',
      'COMPLIANCE_OPS': 'Compliance Operations',
      
      // Client Admin Roles
      'ORG_ADMIN': 'Organization Admin',
      'LMS_ADMIN': 'LMS Admin',
      'COMPLIANCE_ADMIN': 'Compliance Admin',
      
      // Client User Roles
      'ANALYST': 'Analyst',
      'LEARNER': 'Learner',
      'VIEWER': 'Viewer',
      
      // Deprecated Roles (shown during migration)
      'ADMIN': 'Admin (Legacy)',
      'RISK_MANAGER': 'Risk Manager (Legacy)',
      'CLIENT_MANAGER': 'Client Manager (Legacy)',
      'REVIEWER': 'Reviewer (Legacy)',
      'READ_ONLY': 'Read Only (Legacy)',
      'STUDENT': 'Non-Member'
    };
    
    const role = this.getUserRole();
    return roleMap[role] || 'Unknown Role';
  }

  /**
   * Get role description for UI display
   * @param {string} role - Role to get description for
   * @returns {string} Role description
   */
  getRoleDescription(role) {
    const descriptions = {
      'PLATFORM_ADMIN': 'Full platform access including all organizations, billing, data import, compliance configuration, and all proprietary features.',
      'PLATFORM_ENGINEER': 'Technical platform access for development, debugging, integrations, and technical tools. No billing management.',
      'COMPLIANCE_OPS': 'ADI compliance operations. Cross-org check definitions, Check Lab, and hot-lead follow-up. Simplified menu.',
      'ORG_ADMIN': 'Full organization access. Manage users, settings, billing, and branding. Access all modules but cannot configure compliance checks.',
      'LMS_ADMIN': 'Learning Management administrator. Create/manage courses, learning paths, and learners. Access LMS analytics.',
      'COMPLIANCE_ADMIN': 'Compliance administrator. Manage subjects, run reports, make decisions. Cannot configure compliance checks.',
      'ANALYST': 'Compliance analyst. Work with subjects, view reports, and act on gaps in Compliance Health.',
      'LEARNER': 'Learning user. Browse courses, complete training, track progress, earn certifications.',
      'VIEWER': 'Read-only access. View dashboards, reports, and Compliance Health without making changes.',
      // Deprecated roles
      'ADMIN': '[Migrating to Organization Admin] Full organization access.',
      'RISK_MANAGER': '[Migrating to Compliance Admin] Compliance administrator.',
      'CLIENT_MANAGER': '[Migrating to Analyst] Compliance analyst.',
      'REVIEWER': '[Migrating to Viewer] Read-only access.',
      'READ_ONLY': '[Migrating to Viewer] Read-only access.',
      'STUDENT': '[Migrating to Learner] Learning user.',
    };
    return descriptions[role] || 'Unknown role';
  }

  /**
   * Check if user has administrative privileges (platform or organization admin)
   * @returns {boolean}
   */
  isAdmin() {
    return this.hasAnyRole(this.ADMIN_ROLES);
  }

  /**
   * Check if user is an LMS admin (can manage courses, learners)
   * @returns {boolean}
   */
  isLmsAdmin() {
    return this.hasAnyRole(this.LMS_ADMIN_ROLES);
  }

  /**
   * Check if user is a compliance admin (can manage subjects, reports)
   * @returns {boolean}
   */
  isComplianceAdmin() {
    return this.hasAnyRole(this.COMPLIANCE_ADMIN_ROLES);
  }

  /**
   * Check if user can manage projects/clients/subjects
   * @returns {boolean}
   */
  canManage() {
    return this.hasAnyRole(this.COMPLIANCE_ADMIN_ROLES);
  }

  /**
   * Check if user has read-write access (not read-only)
   * @returns {boolean}
   */
  canWrite() {
    return this.hasAnyRole(this.WRITE_ROLES);
  }

  /**
   * Check if user can access LMS features
   * @returns {boolean}
   */
  canAccessLms() {
    return this.hasAnyRole(this.LMS_ACCESS_ROLES);
  }

  /**
   * Check if user can access compliance features
   * @returns {boolean}
   */
  canAccessCompliance() {
    return this.hasAnyRole(this.COMPLIANCE_ACCESS_ROLES);
  }

  /**
   * Check if user can access a specific feature
   * @param {string} featureName - Feature to check access for
   * @returns {boolean}
   */
  canAccessFeature(featureName) {
    // Platform users have access to everything
    if (this.isPlatformUser()) {
      return true;
    }
    
    // Check organization-level feature access
    const org = this.getUserOrganization();
    if (!org || !org.features_enabled) {
      return false;
    }
    
    return org.features_enabled[featureName] || false;
  }

  /**
   * Check if user can access proprietary features (data import, check config, prompt enhancement)
   * @returns {boolean}
   */
  canAccessProprietaryFeatures() {
    // Only platform users can access proprietary features
    return this.hasPlatformAccess();
  }

  /**
   * Check if user can manage their organization
   * @returns {boolean}
   */
  canManageOrganization() {
    const user = this.getUser();
    if (!user) return false;
    // Check role-based access
    if (this.hasAnyRole(['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'ADMIN'])) {
      return true;
    }
    // Also check the legacy boolean flag
    return user.is_organization_admin || false;
  }

  /**
   * Check if user can invite other users
   * @returns {boolean}
   */
  canInviteUsers() {
    if (this.hasPlatformAccess()) return true;
    if (this.canManageOrganization()) return true;
    // LMS Admin can invite learners, Compliance Admin can invite analysts
    if (this.hasAnyRole(['LMS_ADMIN', 'COMPLIANCE_ADMIN'])) return true;
    return false;
  }

  /**
   * Get list of roles available for assignment by current user
   * @returns {Array} Array of {value, label, description} objects
   */
  getAssignableRoles() {
    const allRoles = [
      { value: 'ORG_ADMIN', label: 'Organization Admin', description: this.getRoleDescription('ORG_ADMIN'), tier: 'admin' },
      { value: 'LMS_ADMIN', label: 'LMS Admin', description: this.getRoleDescription('LMS_ADMIN'), tier: 'admin' },
      { value: 'COMPLIANCE_ADMIN', label: 'Compliance Admin', description: this.getRoleDescription('COMPLIANCE_ADMIN'), tier: 'admin' },
      { value: 'ANALYST', label: 'Analyst', description: this.getRoleDescription('ANALYST'), tier: 'user' },
      { value: 'LEARNER', label: 'Learner', description: this.getRoleDescription('LEARNER'), tier: 'user' },
      { value: 'VIEWER', label: 'Viewer', description: this.getRoleDescription('VIEWER'), tier: 'user' },
    ];
    
    // Platform admins can also assign platform roles
    if (this.hasPlatformAccess()) {
      allRoles.unshift(
        { value: 'PLATFORM_ADMIN', label: 'Platform Admin', description: this.getRoleDescription('PLATFORM_ADMIN'), tier: 'platform' },
        { value: 'PLATFORM_ENGINEER', label: 'Platform Engineer', description: this.getRoleDescription('PLATFORM_ENGINEER'), tier: 'platform' }
      );
    }
    
    // LMS Admin can only assign LMS-related roles
    if (this.hasRole('LMS_ADMIN') && !this.hasAnyRole(['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'ADMIN'])) {
      return allRoles.filter(r => ['LEARNER'].includes(r.value));
    }
    
    // Compliance Admin can only assign compliance-related roles
    if (this.hasRole('COMPLIANCE_ADMIN') && !this.hasAnyRole(['PLATFORM_ADMIN', 'PLATFORM_ENGINEER', 'ORG_ADMIN', 'ADMIN'])) {
      return allRoles.filter(r => ['ANALYST', 'VIEWER'].includes(r.value));
    }
    
    return allRoles;
  }

  /**
   * Get user's project ID for data filtering (if any)
   * @returns {number|null}
   */
  getUserProjectId() {
    const user = this.getUser();
    return user ? user.project_id : null;
  }

  /**
   * Check if user is restricted to specific project
   * @returns {boolean}
   */
  isProjectRestricted() {
    return !!this.getUserProjectId();
  }

  /**
   * Get organization subscription tier
   * @returns {string|null}
   */
  getSubscriptionTier() {
    const org = this.getUserOrganization();
    return org ? org.subscription_tier : null;
  }

  /**
   * Check if organization is on trial
   * @returns {boolean}
   */
  isOnTrial() {
    return this.getSubscriptionTier() === 'TRIAL';
  }

  // === MEMBER DISCOUNT METHODS ===

  /**
   * Check if current user is a verified SCAV member
   * @returns {boolean}
   */
  isVerifiedMember() {
    const user = this.getUser();
    return user?.is_verified_member || false;
  }

  /**
   * Get member discount percentage
   * @returns {number} Discount percentage (0-100)
   */
  getMemberDiscountPercentage() {
    const user = this.getUser();
    return user?.member_discount_percentage || 0;
  }

  /**
   * Check if the current member still has their annual free course available.
   * Returns false for non-members or if the benefit has already been used this year.
   * @returns {boolean}
   */
  hasAnnualFreeCourseAvailable() {
    const user = this.getUser();
    return user?.annual_free_course_available || false;
  }

  /**
   * Calculate discounted price for current user
   * @param {number} originalPrice - Original price
   * @returns {Object} { originalPrice, discountPercentage, discountAmount, finalPrice, isMember }
   */
  calculateMemberPrice(originalPrice) {
    const price = parseFloat(originalPrice) || 0;
    const discountPct = this.getMemberDiscountPercentage();
    const isMember = discountPct > 0;
    
    if (isMember) {
      const discountAmount = (price * discountPct / 100);
      const finalPrice = price - discountAmount;
      return {
        originalPrice: price,
        discountPercentage: discountPct,
        discountAmount: Math.round(discountAmount * 100) / 100,
        finalPrice: Math.round(finalPrice * 100) / 100,
        isMember: true
      };
    }
    
    return {
      originalPrice: price,
      discountPercentage: 0,
      discountAmount: 0,
      finalPrice: price,
      isMember: false
    };
  }

  /**
   * Get organization feature limits/capabilities
   * @returns {Object}
   */
  getOrganizationCapabilities() {
    const org = this.getUserOrganization();
    if (!org) return {};
    
    const tier = org.subscription_tier;
    const capabilities = {
      'PLATFORM': {
        maxUsers: 999999,
        maxProjects: 999999,
        advancedAnalytics: true,
        promptEngineering: true,
        multiTenantManagement: true,
        allFeatures: true
      },
      'ENTERPRISE': {
        maxUsers: 100,
        maxProjects: 1000,
        advancedAnalytics: true,
        promptEngineering: false,
        multiTenantManagement: false,
        allFeatures: false
      },
      'PROFESSIONAL': {
        maxUsers: 25,
        maxProjects: 100,
        advancedAnalytics: true,
        promptEngineering: false,
        multiTenantManagement: false,
        allFeatures: false
      },
      'STANDARD': {
        maxUsers: 10,
        maxProjects: 50,
        advancedAnalytics: false,
        promptEngineering: false,
        multiTenantManagement: false,
        allFeatures: false
      },
      'TRIAL': {
        maxUsers: 3,
        maxProjects: 5,
        advancedAnalytics: false,
        promptEngineering: false,
        multiTenantManagement: false,
        allFeatures: false
      }
    };
    
    return capabilities[tier] || capabilities['STANDARD'];
  }
}

export default new AuthService();